import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, CheckCircle2, Loader2, AlertTriangle, KeyRound, Lock } from "lucide-react";
import { toast } from 'react-toastify';
import axios from 'axios';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [token, setToken] = useState(null);
  const [status, setStatus] = useState("verifying"); // verifying, valid, invalid
  const [errorMessage, setErrorMessage] = useState("");
  const [inviteData, setInviteData] = useState(null);
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ 
    fullName: "", 
    tempPassword: "", 
    newPassword: "",
    confirmPassword: ""
  });

  // 1. VERIFY TOKEN ON MOUNT
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      setStatus("invalid");
      setErrorMessage("No invitation token found in URL.");
      return;
    }
    
    setToken(tokenParam);
    verifyToken(tokenParam);
  }, [location]);

  const verifyToken = async (tkn) => {
    try {
      const res = await axios.get(`https://api.testoraglobal.in/api/user/invite/verify?token=${tkn}`);
      if (res.data.success) {
        setInviteData(res.data);
        setStatus("valid");
      }
    } catch (err) {
      setStatus("invalid");
      setErrorMessage(err.response?.data?.message || "This invitation link is invalid or has expired.");
    }
  };

  // 2. REGISTRATION SUBMIT
  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.tempPassword || !formData.newPassword) {
      return toast.error("Please fill all fields");
    }
    if (formData.newPassword.length < 6) {
      return toast.error("New password must be at least 6 characters.");
    }
    if (formData.newPassword !== formData.confirmPassword) {
      return toast.error("New passwords do not match.");
    }

    setLoading(true);
    try {
      const res = await axios.post("https://api.testoraglobal.in/api/user/invite/register", {
        token: token,
        full_name: formData.fullName,
        temp_password: formData.tempPassword,
        new_password: formData.newPassword
      });

      if (res.data.success) {
        toast.success(res.data.message);
        navigate("/login");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Activation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] text-white px-4 relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-[-200px] left-[-200px] w-[500px] h-[500px] bg-purple-600 rounded-full blur-[150px] opacity-20"></div>
      
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md z-10">
        
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg"><FlaskConical size={20} /></div>
            <h1 className="text-3xl font-bold tracking-wide">TESTORA</h1>
          </div>
        </div>

        <div className="bg-[#0B1220]/80 backdrop-blur-2xl border border-gray-800 rounded-2xl p-8 shadow-2xl">
          <AnimatePresence mode="wait">
            
            {/* STATE 1: VERIFYING */}
            {status === "verifying" && (
              <motion.div key="verifying" className="text-center py-10">
                <Loader2 size={40} className="animate-spin text-purple-500 mx-auto mb-4" />
                <p className="text-gray-400">Verifying secure invitation...</p>
              </motion.div>
            )}

            {/* STATE 2: INVALID/EXPIRED */}
            {status === "invalid" && (
              <motion.div key="invalid" className="text-center py-6">
                <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4"><AlertTriangle size={32} /></div>
                <h2 className="text-2xl font-bold mb-2">Link Expired</h2>
                <p className="text-gray-400 mb-6">{errorMessage}</p>
                <button onClick={() => navigate("/login")} className="w-full bg-[#111827] border border-gray-700 hover:border-gray-500 py-3 rounded-lg transition">Return to Login</button>
              </motion.div>
            )}

            {/* STATE 3: VALID - SHOW FORM */}
            {status === "valid" && (
              <motion.div key="valid" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-2xl font-semibold mb-2">Activate Account</h2>
                <p className="text-gray-400 text-sm mb-6 flex items-center gap-2"><CheckCircle2 size={16} className="text-green-500"/> Invited as: <strong className="text-white">{inviteData?.email}</strong></p>

                <form onSubmit={handleRegister}>
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Full Name</label>
                    <input type="text" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} disabled={loading} className="w-full bg-[#111827] border border-gray-700 rounded-lg px-4 py-3 outline-none focus:border-purple-500" placeholder="John Doe" />
                  </div>
                  
                  <div className="mb-6 p-4 bg-purple-500/5 border border-purple-500/20 rounded-xl">
                    <label className="block text-sm text-purple-400 font-semibold mb-2 flex items-center gap-2"><KeyRound size={14}/> Temporary Password</label>
                    <p className="text-xs text-gray-500 mb-3">Check your email for the 8-character temporary password.</p>
                    <input type="text" value={formData.tempPassword} onChange={(e) => setFormData({...formData, tempPassword: e.target.value})} disabled={loading} className="w-full bg-[#111827] border border-gray-700 rounded-lg px-4 py-3 outline-none focus:border-purple-500" placeholder="Enter temporary password" />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2"><Lock size={14}/> Create New Password</label>
                    <input type="password" value={formData.newPassword} onChange={(e) => setFormData({...formData, newPassword: e.target.value})} disabled={loading} className="w-full bg-[#111827] border border-gray-700 rounded-lg px-4 py-3 outline-none focus:border-purple-500" placeholder="••••••••" />
                  </div>

                  <div className="mb-8">
                    <label className="block text-sm text-gray-400 mb-2">Confirm New Password</label>
                    <input type="password" value={formData.confirmPassword} onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} disabled={loading} className="w-full bg-[#111827] border border-gray-700 rounded-lg px-4 py-3 outline-none focus:border-purple-500" placeholder="••••••••" />
                  </div>

                  <button type="submit" disabled={loading} className="w-full bg-purple-600 hover:bg-purple-500 text-white py-3 rounded-lg font-semibold flex justify-center">{loading ? <Loader2 className="animate-spin"/> : "Activate Account & Login"}</button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}